<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import SaveToFolderModal from '@/components/SaveToFolderModal.vue'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper.vue'
import ResourceDownloadButton from '@/resources/ResourceDownloadButton.vue'
import ResourcePreviewModal from '@/resources/ResourcePreviewModal.vue'
import ResourceTileTypeIcon from '@/resources/ResourceTileTypeIcon.vue'
import { useUserStore } from '@/authentication/user.store'
import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import type { AnalyticsData } from '@/tracking/analytics/analyticsTracker'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import type { Resource } from '@/resources/resource_types'
import { tileTitles } from '@/resources/resource_tile_titles'
import { liveEventData } from '@/resources/live_event_data'

const props = withDefaults(
  defineProps<{
    resource: Resource
    position?: number | null
    evtData?: AnalyticsData
  }>(),
  {
    position: null,
    evtData: () => ({})
  }
)

const analytics = getAnalyticsTracker().getChildContext({
  resource_preview_type: 'card',
  position: computed(() => props.position),
  ref: 'home',
  ...liveEventData(() => props.evtData),
  resource_uuid: computed(() => props.resource.uuid),
  resource_title: computed(() => props.resource.title),
  resource_language: computed(() => props.resource.language ?? null)
})

const { authInfo } = storeToRefs(useUserStore())
const isSaveAllowed = computed(
  () => !actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.RESOURCE, {}, authInfo.value).block
)

const setNumber = computed(() => props.resource.set_number ?? 0)
const hasSetNumber = computed(() => setNumber.value > 0)
const screenshots = computed(() => props.resource.screenshots ?? [])
const hasPreview = computed(() => screenshots.value.length > 0)
const titles = computed(() => tileTitles(props.resource.type ?? '', props.resource.title, setNumber.value))

const isPreviewOpen = ref(false)
const isSaveModalOpen = ref(false)

// Safari does not focus a clicked button, and the preview returns focus to whatever had it when it opened.
const openPreview = (event: MouseEvent) => {
  const previewButton = event.currentTarget as HTMLElement
  previewButton.focus()
  isPreviewOpen.value = true
  analytics.sendEvent(
    EVENT_NAMES.RESOURCES.SHOWED_PREVIEW,
    { resource_preview_type: 'modal', resource_language: props.resource.language ?? null },
    { cu: props.resource.uuid, ct: props.resource.title }
  )
}

const openSaveModal = () => {
  isSaveModalOpen.value = true
  analytics.sendEvent(EVENT_NAMES.RESOURCES.CLICKED_SAVE)
}
</script>

<template>
  <div
    class="group relative mb-4 mr-6 mt-3 h-[119px] w-44 border border-gray-400 bg-white"
    data-testid="resource-tile"
  >
    <div class="flex h-full w-full justify-center overflow-hidden">
      <IKImageS3Wrapper :aws-url="resource.thumbnail_url ?? ''" alt="" :transformation="[{ width: 150 }]" />
    </div>

    <span
      v-if="hasSetNumber"
      class="absolute right-2 top-2 rounded-xs bg-gray-200 px-1.5 text-sm font-medium text-gray-900 group-focus-within:invisible group-hover:invisible group-has-[[data-state=open]]:invisible"
      data-testid="resource-tile-set-number"
    >
      Set {{ setNumber }}
    </span>

    <div
      class="absolute bottom-0 left-0 flex h-8 w-full items-center bg-gray-200 group-focus-within:invisible group-hover:invisible group-has-[[data-state=open]]:invisible"
      :aria-hidden="hasPreview || undefined"
      data-testid="resource-tile-footer"
    >
      <ResourceTileTypeIcon :is-data-tracking-sheet="titles.isDataTrackingSheet" />
      <span class="truncate font-medium" data-testid="resource-tile-title">{{ titles.footerTitle }}</span>
    </div>

    <button
      v-if="hasPreview"
      type="button"
      class="pointer-events-none absolute inset-0 flex items-start bg-gray-300 px-1 pb-2.5 pt-1.5 text-left opacity-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 group-has-[[data-state=open]]:opacity-100"
      :aria-label="`Open preview for ${titles.previewLabel}`"
      data-testid="resource-tile-preview"
      @click="openPreview"
    >
      <ResourceTileTypeIcon :is-data-tracking-sheet="titles.isDataTrackingSheet" />
      <span class="min-w-0 truncate pt-1" aria-hidden="true">{{ titles.hoverTitle }}</span>
    </button>

    <div
      class="pointer-events-none absolute bottom-2 left-4 flex gap-1 whitespace-nowrap opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 group-has-[[data-state=open]]:opacity-100"
    >
      <ResourceDownloadButton :resource="resource" :condense-buttons="true" :show-tooltips="true" />
      <GbButton
        v-if="isSaveAllowed"
        gbTheme="greenPrimary"
        size="small"
        class="!p-1"
        tooltipText="Save"
        :aria-label="`Save ${titles.titleWithSet}`"
        data-testid="resource-tile-save"
        @click="openSaveModal"
      >
        <span class="material-icons-round text-display-sm" aria-hidden="true">save</span>
      </GbButton>
    </div>

    <ResourcePreviewModal
      v-if="isPreviewOpen"
      :uuid="resource.uuid"
      :title="resource.title"
      :type="resource.type"
      :download-links="resource.download_links"
      :google-drive-links="resource.google_drive_links"
      :screenshots="screenshots"
      :num-pages="resource.num_pages"
      :language="resource.language"
      @update:visible="isPreviewOpen = $event"
    />
    <SaveToFolderModal
      v-if="isSaveModalOpen"
      :content-uuid="resource.uuid"
      content-type="resource"
      content-type-label="Resource"
      @update:visible="isSaveModalOpen = $event"
    />
  </div>
</template>
